import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CopyButtonProps {
  /** The string value to write to the clipboard. */
  value: string | null | undefined;
  /** Optional label used in the title/tooltip, e.g. "email" → "Copy email". */
  label?: string;
  /** Additional class names applied to the button. */
  className?: string;
  /** Icon size; defaults to xs (h-3 w-3). */
  size?: 'xs' | 'sm';
}

/**
 * Tiny inline copy-to-clipboard button. Shows a transient check icon for
 * ~1.5s after a successful copy. Renders nothing if `value` is empty.
 */
export function CopyButton({ value, label, className, size = 'xs' }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  if (!value) return null;

  const iconSize = size === 'xs' ? 'h-3 w-3' : 'h-3.5 w-3.5';

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={copied ? 'Copied!' : label ? `Copy ${label}` : 'Copy'}
      aria-label={label ? `Copy ${label}` : 'Copy'}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-300',
        className,
      )}
    >
      {copied ? (
        <Check className={cn(iconSize, 'text-emerald-500')} />
      ) : (
        <Copy className={iconSize} />
      )}
    </button>
  );
}
